import { useMemo } from 'react';
import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { Loading } from '../../components/Loading';
import { useFindAllDocumentTemplatesQuery } from '../../pages/DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import type { MissionQuotationWithItsInfo } from '../../pages/MissionQuotation/types/mission-quotation-with-its-info.type';

import { MissionQuotationInfoStateForm_ResolvableMissionType_Fragment } from '../MissionQuotationInfoState';
import type { FormVariant } from '../enums/variant.enum';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { getMissionQuotationInfoSchema } from './schemas/mission-quotation-info.schema';
import { MissionQuotationInfoFormFields } from './MissionQuotationInfoFormFields';

interface Props {
  readonly formVariant: FormVariant;
  readonly initialValues?: MissionQuotationWithItsInfo;
  readonly onCancel?: () => void;
  readonly onSuccess?: () => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel?: string;
  readonly useSubmitFactory: (
    handleSuccess?: () => void,
  ) => (
    quotationWithItsInfo: MissionQuotationWithItsInfo,
    formikHelpers: FormikHelpers<MissionQuotationWithItsInfo>,
  ) => void;
}

export const MissionQuotationInfoForm = ({
  formVariant,
  initialValues: pInitialValues,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton,
  submitLabel = 'Valider',
  useSubmitFactory,
}: Readonly<Props>) => {
  const { genericStatusesCodeMap, quotationWorkflows } = useReferencesContext();

  const { missionId, resolvableMissionType } = useMissionReadyContext();

  const { data, isSuccess } = useFindAllDocumentTemplatesQuery();

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const allDocumentTemplates = data?.findAllDocumentTemplates;

  const quotationTemplates =
    allDocumentTemplates?.filter(
      (template) =>
        (template.statusId === genericStatusesCodeMap.Enabled._id ||
          template._id === initialValues.templateId) &&
        template.document.typeId === initialValues.typeId,
    ) ?? EMPTY_ARRAY;

  const quotationTemplateId = getFragmentData(
    MissionQuotationInfoStateForm_ResolvableMissionType_Fragment,
    resolvableMissionType,
  )?.quotationTemplateId;

  const suggestedTemplateIds = useMemo(
    () =>
      new Set<Id>(quotationTemplateId ? [quotationTemplateId] : EMPTY_ARRAY),
    [quotationTemplateId],
  );

  const validationSchema = useMemo(
    () => getMissionQuotationInfoSchema(formVariant, quotationWorkflows),
    [formVariant, quotationWorkflows],
  );

  return (
    <>
      {!isSuccess && <Loading />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={useSubmitFactory(handleSuccess)}
        validationSchema={validationSchema}
      >
        <MissionQuotationInfoFormFields
          formVariant={formVariant}
          missionId={missionId}
          onCancel={handleCancel}
          quotationTemplates={quotationTemplates}
          setMustBlockAction={setMustBlockAction}
          showCancelButton={showCancelButton}
          submitLabel={submitLabel}
          suggestedTemplateIds={suggestedTemplateIds}
        />
      </Formik>
    </>
  );
};
